import {
  Component,
  Input,
  OnInit,
  ViewChild,
  AfterViewChecked,
  ElementRef,
  ViewContainerRef,
  TemplateRef,
  OnDestroy, EventEmitter, ChangeDetectorRef,
} from '@angular/core';
import { Node, Options, TreeTableNode } from './ng-material-treetable/models';
import { AggregationMethod } from '../grouped-result-table/grouped-result-table.component';
import Utils from '../../../util';
import { ProjectCommunicationService } from '../../../communication/project-comms.service';
import { TreetableComponent } from './ng-material-treetable/component/treetable.component';
import { AnessaTranslationService } from '../../../helper/translation/anessa-translation.service';
import { GetNameDialogComponent } from '../get-name.dialog';
import { take, takeUntil, takeWhile } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { InputSummary } from '../../../models/input-summary';
import { Unit, UnitService } from '../../../helper/anessa-library/anessa-library/projects/unit-library/src/public-api';
import { isEmpty } from 'lodash-es';
import { PeriodUnitMapper } from '../../results-util';
import {
  DisplayOption
} from '../../../models/results/baseline/baseline-summary-display-option/baseline-summary-display-option.model';
import { UserpilotReloader } from '../../../helper/userpilot-reloader.component';
import FormatUtil from '../../../helper/format.util';
import { LooseObject } from '../../../models/loose-object.model';
import { ResultRow } from '../../../models/data-structures/results/result-row.model';

@Component({
  selector: 'results-treetable',
  templateUrl: './results-treetable.component.html',
  styleUrls: ['./results-treetable.component.scss']
})
export class ResultsTreetableComponent extends UserpilotReloader implements OnInit, AfterViewChecked, AfterViewChecked, OnDestroy {

  @ViewChild('treeTable') treeTable?: TreetableComponent<any>;
  @ViewChild('treeTable', { read: ElementRef }) treeTableElement?: ElementRef<HTMLElement>;
  @ViewChild('treeTableView', { read: ViewContainerRef }) treeTableView?: ViewContainerRef;
  @ViewChild('treeTableTemplate', { read: TemplateRef }) treeTableTemplate?: TemplateRef<any>;
  htmlRows: HTMLCollection | null = null;
  lastTableSize = 0;
  lastNodeClicked: TreeTableNode<any> | null = null;
  styleTreeTable = false;
  _tableData: LooseObject | null = null;

  private incomingResultsData: LooseObject = {};
  _resultsData: LooseObject | null = null;
  @Input() set resultsData(data: LooseObject) { this.processResultsData(data); }
  _inputsData: InputSummary | null = null;
  @Input() set inputsData(data: InputSummary) { this.processInputsData(data); }
  @Input() translateHeaders: string[] = [];
  tableRendered = false;
  @Input() hideOnlyZeros = false;
  @Input() groupAggregationMethod: AggregationMethod = AggregationMethod.NONE;
  @Input() aggregationBlackList: string[] = [];
  @Input() aggregationWhiteList: string[] = [];
  @Input() customColumnOrder?: string[];
  @Input() idPrefix = '';

  /**
   * Used to specify a specific property in the dataset to use as the label
   */
  @Input() labelKey = 'RESULTS.DATA.LABEL';
  /**
   * Used to specify a specific property in the dataset to use as the value(s)
   * ie: feedstock breakdown value prop is 'RESULTS.GENERAL.AMOUNT'
   */
  @Input() valueKey = 'RESULTS.DATA.VALUES';

  aggregators = {
    1: (a: number, b: number) => (a || 0) + (b || 0)
  };
  aggregator = null;
  @Input() firstColumnHeader = '';
  @Input() dataFormat = 'mixed'; // mixed, value_unit, yearly
  @Input() set currencyUnit(currencyUnit: Unit) { this.setCurrencyUnit(currencyUnit); }
  _currencyUnit: Unit | null = null;
  _lastCurrencyUnit: Unit | null = null;
  @Input() set measurement(measurement: string) { this.setMeasurement(measurement); }
  _measurement: string = '';
  _lastMeasurement: string = '';
  measures = [
    { metric: '000000000000000000003601', imperial: '000000000000000000003604' }, // kg VS/m3/day, lb VS/ft3/day
    { metric: '000000000000000000001203', imperial: '000000000000000000001204' }, // t (tonne), t (short ton)
    { metric: '000000000000000000004901', imperial: '000000000000000000004902' }, // ton/day, short ton/day
    { metric: '000000000000000000004905', imperial: '000000000000000000004906' }, // kg VS/m3/day, lb VS/ft3/day
    { metric: '000000000000000000001802', imperial: '000000000000000000001803' }, // m3, ft3
    { metric: '000000000000000000003803', imperial: '000000000000000000003806' }, // m3/day, ft3/day
    { metric: '000000000000000000003805', imperial: '000000000000000000003804' }, // Nm3/day, scf/day
    // {metric: '000000000000000000003814', imperial: '000000000000000000003811'}, // GJ/day, GGE/day
    // {metric: '000000000000000000003815', imperial: '000000000000000000003810'}, // GWHr/day, MMBTU/day
    { metric: '000000000000000000005201', imperial: '000000000000000000005203' }, // kg CO2eq/day, lbs CO2eq/day,
    { metric: '000000000000000000001701', imperial: '000000000000000000001702' }, // Celcius, Farenheit
  ];

  flattenList = ['RESULTS.DATA.INSURANCE', 'RESULTS.DATA.INTEREST'];
  noFlattenList = ['RESULTS.DATA.ISPL.AI'];

  @Input() set period(period: string) { this.setPeriod(period); }
  _period: string | null = null;
  _lastPeriod: string | null = null;

  @Input() hideSaveButtons = false;

  tree: Node<any>[] = [];
  options: Options<any> = {
    capitalisedHeader: false,
    elevation: 1
  };
  intKeys: Set<string> = new Set();
  customColumns: Set<string> = new Set();

  precision: number = -1;
  trigger?: string;
  savedTableName: string = '';

  currencies: Unit[] = [];
  currenciesPerYear: Unit[] = [];
  onDestroyNotifier = new EventEmitter();

  constructor(
    private changeDetector: ChangeDetectorRef,
    private projectComService: ProjectCommunicationService,
    private toasterService: ToastrService,
    private translation: AnessaTranslationService,
    private dialog: MatDialog,
    private unitService: UnitService
  ) {
    super();
    this.precision = this.projectComService.getPrecision() ?? -1;
  }

  ngOnInit(): void {
    this.aggregator = this.aggregators[this.groupAggregationMethod];
    this.translation.languageChangeNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(() => {
        this.trigger = this.translation.getCurrentLang();
        this.reprocessData();
      });
    this.unitService.unitsLoadedNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .pipe(takeWhile(x => !x, true))
      .subscribe(() => {
        this.currencies = this.unitService.getUnitsByUnitTypeName('Currency');
        this.currenciesPerYear = this.unitService.getUnitsByUnitTypeId('000000000000000000000022');
      });
  }

  ngOnDestroy(): void {
    this.onDestroyNotifier.emit();
    this.onDestroyNotifier.unsubscribe();
  }

  ngAfterViewInit() {
    if (this.treeTableView && this.incomingResultsData) {
      this.processResultsData(this.incomingResultsData);
    }
  }

  ngAfterViewChecked() {
    if (!this.tableRendered) { this.rerenderTable(); }
    if (this.treeTable && this.treeTable['treeTable'] && this.treeTableElement) {
      this.htmlRows = (this.treeTableElement.nativeElement.children[0] as HTMLTableElement).rows
      if (this.styleTreeTable) {
        this.collapseRows();
        this.htmlRows[0].children[0].innerHTML = this.firstColumnHeader;
        for (const header of this.htmlRows[0].children) {
          const headerName = header.innerHTML.trim().toLowerCase();
          if (headerName === 'value') {
            header.innerHTML = this.translation.get('RESULTS.DATA.VALUE');
          } else if (headerName === 'unit') {
            header.innerHTML = this.translation.get('RESULTS.DATA.UNIT');
          }
        }
        this.applyClassesToTable();
        this.styleTreeTable = false;
        this.fixEscapedTags();
      }
      if (this.lastTableSize && this.lastTableSize !== this.htmlRows.length) {
        this.styleNodeRecursively(this.lastNodeClicked, true);
        this.lastTableSize = this.htmlRows.length;
        this.fixEscapedTags();
      }
    }
  }

  /** fixes escaped '<' '>' symbols to have inner tags working (like 'm<sup>3</sup>') */
  fixEscapedTags() {
    if (!this.htmlRows) return;
    for (const row of this.htmlRows) {
      for (let cellIndex = 1; cellIndex < row.children.length; cellIndex++) {
        const cell = row.children[cellIndex];
        if (cell.children.length === 0) { continue; }
        let html = cell.children[0].innerHTML;
        html = html.replaceAll('&lt;', '<');
        html = html.replaceAll('&gt;', '>');
        cell.children[0].innerHTML = html;
      }
    }
  }

  collapseRows() {
    if (!this.treeTable) return;
    this.treeTable['treeTable'].forEach(row => {
      if (row.depth > 0) {
        row.isExpanded = false;
      }
    });
    this.treeTable['foldTree']();
  }

  applyClassesToTable() {
    if (!this.treeTable) return;
    for (const node of this.treeTable['treeTable']) {
      if (node.depth === 0) {
        this.styleNodeRecursively(node);
      }
    }
  }

  processTable() {
    // format values
    for (const node of this.tree) { this.formatNodeRecursively(node); }
    // set columns order
    const sortedIntKeys = Array.from(this.intKeys).map(key => parseInt(key, 10))
      .sort((a, b) => a - b).map(key => key.toString());
    if (this.customColumnOrder &&  this.customColumnOrder.length) {
      if (this.labelKey !== 'RESULTS.DATA.LABEL') {
        this.options['customColumnOrder'] = [...this.customColumnOrder];
      } else {
        this.options['customColumnOrder'] = ['RESULTS.DATA.LABEL', ...this.customColumnOrder];
      }
    } else {
      const columnOrder = ['RESULTS.DATA.LABEL'];
      if (this.customColumns.has('RESULTS.DATA.VALUE')) { columnOrder.push('RESULTS.DATA.VALUE'); }
      if (this.customColumns.has('RESULTS.DATA.UNIT')) { columnOrder.push('RESULTS.DATA.UNIT'); }
      columnOrder.push(...sortedIntKeys);
      this.options['customColumnOrder'] = columnOrder;
    }
    // let ngAfterViewChecked() style the table
    this.styleTreeTable = true;
  }

  processResultsData(data: LooseObject | null) {
    if (data && !Utils.isEmpty(data)) {
      this._resultsData = data;
      this.incomingResultsData = data;
      this.intKeys = new Set();
      this.customColumns = new Set();
      this._tableData = this.convertRawResultRecursively(this._resultsData);
      if (!this._tableData) { return; }
      this.tableDataToTree();
      this.processTable();
      this.rerenderTable();
    }
  }

  reprocessData() {
    if (this._resultsData) {
      this.processResultsData(this._resultsData);
    }
    if (this._inputsData) {
      this.processInputsData(this._inputsData);
    }
  }

  reprocessResultsData() {
    this.processResultsData(this._resultsData);
  }

  processInputsData(inputSummary: InputSummary | null) {
    if (inputSummary) {
      this._inputsData = inputSummary;
      const resultsFormat = {};
      this.inputSummaryToResultsRecursively(inputSummary, resultsFormat);
      this.processResultsData(resultsFormat);
    }
  }

  reprocessInputsData() {
    this.processInputsData(this._inputsData);
  }

  inputSummaryToResultsRecursively(inputSummary: InputSummary, result: LooseObject) {
    if (inputSummary.inputs.length === 0) {
      if (isEmpty(inputSummary.subSection)) { return; } // empty input summary
      const subSectionKeys = Object.keys(inputSummary.subSection);
      if (subSectionKeys.length === 1 && subSectionKeys[0] === '') {
        // some results are weird like this (project info, digester)
        inputSummary = inputSummary.subSection[''];
      }
    }
    for (const input of inputSummary.inputs) {
      if (!input) { continue; }
      result[input.name] = new ResultRow({
        'RESULTS.DATA.LABEL': input.name,
        // @ts-ignore TODO: change model to allow values to be string[]
        'RESULTS.DATA.VALUES': [input.value],
        'RESULTS.DATA.UNIT': input.unit,
        'aux.DisplayOption': input['aux.DisplayOption']
      });
    }
    if (inputSummary.subSection && !isEmpty(inputSummary.subSection)) {
      const subSection = inputSummary.subSection;
      for (const key of Object.keys(subSection)) {
        if (key.startsWith('aux.')) { continue; }
        result[key] = {};
        this.inputSummaryToResultsRecursively(subSection[key], result[key]);
      }
    }
  }

  /** (re)render the treetable component */
  rerenderTable() {
    if (this.treeTableView && this.treeTableTemplate) {
      this.treeTableView.clear();
      this.treeTableView.createEmbeddedView(this.treeTableTemplate);
      this.tableRendered = true;
      this.changeDetector.detectChanges();
    }
  }

  tableDataToTree() {
    this.tree = [];
    if (!this._tableData) return;
    for (const i in this._tableData) {
      const node = this.dataToNode(this._tableData[i]);
      if (node) { this.tree.push(node); }
    }
  }

  shouldFlattenResult(key: string) {
    if (this.noFlattenList.includes(key)) return false;
    if (key.includes(".EXPENSES") || key.includes(".REVENUES")) return false
    return this.flattenList.includes(key) || key.includes('.ISPL.');
  }

  convertRawResultRecursively(data: LooseObject, parentAggregator?: AggregationMethod) {
    if (!this.includeResult(data)) { return []; }
    const keys = Object.keys(data);
    if (keys.length === 0) { return null; }
    const leafKeys = [this.labelKey, this.valueKey];
    const nodes = [];
    for (const key of keys) {
      if (key.startsWith('aux.') || key === 'RESULTS.DATA.CREATED') { continue; }
      const params = this.getParamsForData(data, key);
      const label = this.translation.get(key, params, undefined, false);
      const node: LooseObject = {};
      node[this.labelKey] = label !== 'undefined' ? label : key;
      if (this.shouldFlattenResult(key)) {
        node['key'] = key;
      }
      let value = data[key];
      if (!value) { continue; }
      const valueKeys = Object.keys(value).filter(key => !key.includes('aux.'));
      if (valueKeys.length === 1 && valueKeys[0] === key) { value = value[key]; } // for categories with a single node that have the same name as the category
      if (!this.includeResult(value)) { continue; }
      if (leafKeys.every(leafKey => Object.keys(value).includes(leafKey))) { // results object is a leaf
        node[this.valueKey] = Array.isArray(value[this.valueKey]) ? value[this.valueKey] : [value[this.valueKey]];
        if (node[this.valueKey].length >= 1) {
          if (this.customColumnOrder) {
            for (const key of this.customColumnOrder) {
              if (!node[key] && value[key]) node[key] = value[key];
            }
          } else {
            this.convertNodeToSetUnits(value);
            node['RESULTS.DATA.VALUES'] = value['RESULTS.DATA.VALUES'];
            node['RESULTS.DATA.UNIT'] = value['RESULTS.DATA.UNIT'] || '-';
            if (value['INFO_TOOLTIP']) node['INFO_TOOLTIP'] = value['INFO_TOOLTIP'];
          }
        }
      } else if (typeof value === 'object' && value !== null) { // results object is a root or a branch
        // set category aggregation
        let nodeAggregator: AggregationMethod | null = null;
        if (this.groupAggregationMethod !== AggregationMethod.NONE) {
          if (this.aggregationWhiteList.length > 0) {
            if (this.aggregationWhiteList.includes(key)) {
              nodeAggregator = this.groupAggregationMethod;
            } else if (parentAggregator) {
              nodeAggregator = parentAggregator;
            }
          } else if (!this.aggregationBlackList.includes(key)) {
            nodeAggregator = this.groupAggregationMethod;
          }
          if (nodeAggregator) { node['aggregator'] = nodeAggregator; }
        }
        // process result's children recursively
        node['rows'] = this.convertRawResultRecursively(value, nodeAggregator);
      }
      nodes.push(node);
    }
    return nodes;
  }

  convertNodeToSetUnits(data: ResultRow) {
    const unit = data['RESULTS.DATA.UNIT'];
    if (!unit) { return; }
    // convert currency
    if (this._currencyUnit && (!this._lastCurrencyUnit || this._currencyUnit.id !== this._lastCurrencyUnit.id)) {
      const nodeUnit = this.unitService.getUnitById(unit);
      if (!nodeUnit) { return; }
      if (nodeUnit.unitType === '000000000000000000000020') {
        const values = data.values;
        for (let i = 0; i < values.length; i++) {
          values[i] = Unit.convertValue(values[i], nodeUnit.id, this._currencyUnit.id);
        }
        data['RESULTS.DATA.UNIT'] = this._currencyUnit.id;
      } else if (nodeUnit.unitType === '000000000000000000000022') {
        const currencyName = nodeUnit.displayName.split('/')[0];
        if (currencyName !== this._currencyUnit.displayName && this.currencies && this.currenciesPerYear) {
          const currency = this.currencies.find(aCurrency => aCurrency.displayName === currencyName);
          const currencyPerYear = this.currenciesPerYear.find(aCurrency => aCurrency.displayName.includes(this._currencyUnit.displayName));
          if (currency && currencyPerYear) {
            const values = data.values;
            for (let i = 0; i < values.length; i++) {
              values[i] = Unit.convertValue(values[i], currency.id, this._currencyUnit.id);
            }
            data['RESULTS.DATA.UNIT'] = currencyPerYear.id;
          }
        }
      }
    }
    // convert measurement
    if (this._measurement && this._measurement !== this._lastMeasurement) {
      const converter = this.getMeasurementConverter(unit);
      if (converter) {
        const values = data['RESULTS.DATA.VALUES'];
        for (let i = 0; i < values.length; i++) {
          if (unit === '000000000000000000001701') {
            values[i] = ((9 * values[i]) / 5) + 32.0;
          } else if (unit === '000000000000000000001702') {
            values[i] = (values[i] - 32) * 5;
            values[i] = values[i] / 9;
          } else {
            values[i] *= converter.factor;
          }
        }
        data['RESULTS.DATA.UNIT'] = converter.unit;
      }
    }
  }

  getMeasurementConverter(unitId: string): { factor: number, unit: string } {
    let fromMeasure;
    let toMeasure;
    if (this._measurement === 'RESULTS.GENERAL.METRIC') {
      fromMeasure = 'imperial';
      toMeasure = 'metric';
    } else if (this._measurement === 'RESULTS.GENERAL.IMPERIAL') {
      fromMeasure = 'metric';
      toMeasure = 'imperial';
    }
    const toIdPair = this.measures.find(measure => measure[fromMeasure] === unitId);
    if (!toIdPair) { return null; }
    const toId = toIdPair[toMeasure];
    const unit = this.unitService.getUnitById(toId);
    if (!unit) { return null; }
    const factor = Unit.convertValue(1, unitId, toId);
    return { factor: factor, unit: toId };
  }

  includeResult(data): boolean {
    const displayOption: DisplayOption = data['aux.DisplayOption'];
    if (!displayOption) { return true; }
    const toBeShown = displayOption.status === 'checked' || displayOption.status === 'indeterminate';
    const correctFormat = this.dataFormat === 'mixed' || (displayOption.type === this.dataFormat || displayOption.type === 'mixed');
    return toBeShown && correctFormat;
  }

  flattenNode(data: LooseObject, node: Node<any>) {
    // For income statement if there is only one child, don't bother showing the dropdown / children
    if (data.key) {
      if (this.shouldFlattenResult(data.key)) {
        if (node.children.length === 1 && node.children[0].children.length === 0) {
          node.value = node.children[0].value;
          node.children = [];
        }
      }
    }
  }

  dataToNode(data: LooseObject): Node<any> | null {
    const rows = data['data'] || data['rows'];
    const values = data[this.valueKey];
    if (!(rows && rows.length > 0) && !values) { return null; }
    // make new node
    const node: Node<any> = {
      value: {},
      children: []
    };
    node.value[this.labelKey] = data[this.labelKey] || data['name'];
    if (rows) { // add children nodes recursively
      for (const row of rows) {
        const rowNode = this.dataToNode(row);
        if (!rowNode) {
          continue;
        }
        if (rowNode) { node.children.push(rowNode); }
        // aggregate values
        let removeFirstItem = false;
        for (const key of Object.keys(rowNode.value)) {
          if (this.isIntKey(key) || key === 'RESULTS.DATA.VALUES' || key === 'RESULTS.DATA.VALUE' || key === 'RESULTS.DATA.UNIT') {
            if (data['aggregator']) {
              if (data['aggregator'] === AggregationMethod.FIRST && rows.indexOf(row) === 0) {
                if (node.children?.length > 0) {
                  node.value[key] = node.children[0]?.value[key];
                  removeFirstItem = true;
                }
              } else {
                const nodeAggregator = this.aggregators[data['aggregator']];
                if (nodeAggregator) {
                  node.value[key] = nodeAggregator(node.value[key], rowNode.value[key]);
                }
              }
            } else {
              node.value[key] = null;
            }
            if (this.isIntKey(key)) {
              this.intKeys.add(key);
            }
          } else if (key !== this.labelKey) {
            node.value[key] = null;
          }
        }
        if (removeFirstItem) {
          // remove this child item
          node.children.splice(0, 1);
        }
      }
      for (const column of [...this.intKeys, ...this.customColumns]) {
        if (!(node.value.hasOwnProperty(column))) {
          node.value[column] = '';
        }
      }
    } else if (values) { // add values if it's a leaf
      if (this.hideOnlyZeros && this.valuesOnlyContainsZeros(values)) {
        return null;
      }
      if (values.length === 1 && this.customColumnOrder) {
        for (const key of this.customColumnOrder) {
          if (data[key]) {
            node.value[key] = data[key];
          } else if (key === 'RESULTS.DATA.VALUE' && values) {
            node.value[key] = this.translateValues(values);
          }
          if (key === 'RESULTS.DATA.UNIT') node.value['RESULTS.DATA.UNIT'] = data['RESULTS.DATA.UNIT'] || '-';
        }
      } else if (values.length === 1 && data['RESULTS.DATA.UNIT']) { // table with value and unit columns (like technical results)
        const valueAndUnit = this.formatValueAndUnit(values, data['RESULTS.DATA.UNIT']);
        node.value = { ...node.value, ...valueAndUnit };
        if (data['INFO_TOOLTIP']) node.value['INFO_TOOLTIP'] = data['INFO_TOOLTIP'];
      } else { // table with yearly results (like income statement)
        node.value = { ...node.value, ...values };
      }
    }
    if (!values && node.children && node.children.length === 0) {
      return null;
    }

    this.flattenNode(data, node);
    return node;
  }

  translateValues(values: any[]) {
    if (!values || values.length < 1) return values;
    for (let i = 0; i < values.length; i++) {
      if (Utils.isString(values[i])) {
        values[i] = this.translation.get(values[i]);
      }
    }
    return values;
  }

  formatValueAndUnit(values: (number | string)[], unit: string) {
    // format value
    let formattedValue: any = values[0];
    const isNumeric = Utils.isNumeric(formattedValue);
    if (!isNumeric && formattedValue) { formattedValue = this.translation.get(values[0].toString(), {}, undefined, false); }
    // format unit
    let formattedUnit;
    const unitById = this.unitService.getUnitById(unit);
    if (unitById) {
      formattedUnit = Unit.parseUnitDisplayName(this.translation.get(unitById.translationId));
      // convert period
      if (this._period && this._period !== this._lastPeriod && isNumeric) {
        const converter = this.getPeriodConverter(unitById);
        if (converter) {
          formattedValue *= converter.to / converter.from;
          formattedUnit = converter.newDisplayName;
        }
      }
    } else {
      formattedUnit = Unit.parseUnitDisplayName(this.translation.get(unit, {}, undefined, false));
    }
    // make sure value and unit columns will be displayed
    this.customColumns.add('RESULTS.DATA.VALUE');
    this.customColumns.add('RESULTS.DATA.UNIT');
    // return value and unit
    return { 'RESULTS.DATA.VALUE': formattedValue, 'RESULTS.DATA.UNIT': formattedUnit };
  }

  // TODO same unit display name problem
  getPeriodConverter(unit: Unit): { from: number, to: number, newDisplayName: string } | null {
    let sourceFactor: number | null = null;
    const displayName = unit.displayName.toLowerCase();
    if (displayName.includes('/day')) {
      sourceFactor = 1;
    } else if (displayName.includes('/month')) {
      sourceFactor = 30;
    } else if (displayName.includes('/year')) {
      sourceFactor = 365;
    }
    if (sourceFactor === null) return null;

    const unitHackTranslation = PeriodUnitMapper[unit.id];
    if (!unitHackTranslation) {
      console.log('add this to the unit hack: ' + unit.id + ' - ' + unit.displayName);
    }
    const periodTranslation = this.translation.get(this._period);

    let resultFactor;
    if (this._period === 'RESULTS.GENERAL.DAY') {
      resultFactor = 1;
    } else if (this._period === 'RESULTS.GENERAL.MONTH') {
      resultFactor = 30;
    } else if (this._period === 'RESULTS.GENERAL.YEAR') {
      resultFactor = 365;
    }
    if (!sourceFactor || !resultFactor || sourceFactor === resultFactor) { return null; }

    const newDisplayName = Unit.parseUnitDisplayName(
      this.translation.get(unitHackTranslation, { period: periodTranslation })
    );
    return { from: sourceFactor, to: resultFactor, newDisplayName: newDisplayName };
  }

  isIntKey(key: string): boolean {
    return Number.isInteger(parseInt(key, 10));
  }

  formatNodeRecursively(node: Node<any>) {
    for (const key of Object.keys(node.value)) {
      // if (this.isIntKey(key) || (key !== this.labelKey && !isNaN(node.value[key]))) {
      //   node.value[key] = this.formatNumber(node.value[key]);
      const value = node.value[key];
      if ((key !== this.labelKey || this.isIntKey(key)) && (value || value === 0)) {
        if (value.length === 24 && this.isIntKey(value)) { // value is a unit id
          const unit = this.unitService.getUnitById(value);
          if (unit) { node.value[key] = Unit.parseUnitDisplayName(this.translation.get(unit.translationId)); }
        } else {
          node.value[key] = this.formatNumber(value);
        }
      }
    }
    if (node.children) {
      for (const childNode of node.children) {
        this.formatNodeRecursively(childNode);
      }
    }
  }

  formatNumber(input) {
    if (input === ' ' || input === undefined || input === null) {
      return '';
    }
    if (!Utils.isNumeric(input)) {
      return input;
    }
    let displayValue: string;
    if (this.precision > -1) {
      displayValue = String(Utils.roundToPrecisionFixed(input, this.precision));
    } else {
      displayValue = input.toString();
    }
    if (displayValue != null) {
      const numberComponents = displayValue.split('.');
      const leftSide = numberComponents[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      numberComponents.splice(0, 1, leftSide);
      displayValue = numberComponents.join('.');
    }
    return displayValue;
  }

  nodeClicked(node: TreeTableNode<any>) {
    if (!this.treeTableElement) return;
    this.lastTableSize = (this.treeTableElement.nativeElement.children[0] as HTMLTableElement).rows.length;
    this.lastNodeClicked = node;
  }

  styleNodeRecursively(node: TreeTableNode<any> | null, byClick = false) {
    if (!node || !this.treeTable || !this.htmlRows) return;
    // style node
    let rowIndex = this.treeTable['treeTable'].indexOf(node) + 1;
    if (byClick) {
      for (let i = 0; i < this.treeTable['treeTable'].indexOf(node); i++) {
        if (!this.treeTable['treeTable'][i].isVisible) {
          rowIndex--;
        }
      }
    }
    if (node.children.length === 0) {
      this.htmlRows[rowIndex].children[0].classList.add('treetable-row-leaf');
    } else if (node.depth === 0) {
      for (const td of this.htmlRows[rowIndex].children) {
        td.classList.add('treetable-row-root');
      }
      this.htmlRows[rowIndex].children[0].classList.add('treetable-row-root-label');
    } else {
      for (const td of this.htmlRows[rowIndex].children) {
        td.classList.add('treetable-row-branch');
      }
    }
    // style its children
    if (node.isExpanded) {
      for (const childNode of node.children) {
        this.styleNodeRecursively(childNode, byClick);
      }
    }
  }

  openSaveDialog(isSaveToReport: boolean) {
    const dialogRef = this.dialog.open(GetNameDialogComponent, {
      width: 'max-content',
      data: { name: this.savedTableName, instruction: this.translation.get('RESULTS.GENERAL.INPUT_TABLE_NAME') }
    });
    dialogRef.afterClosed().pipe(take(1)).subscribe(result => {
      if (result && result.saveClicked) {
        if (result.name !== undefined) {
          const reportTable = this.makePlainHtmlTable();
          this.savedTableName = result.name;
          const fileContent = this.generateCSVData();
          if (!fileContent) {
            console.warn("failed to generate csv data");
            return;
          }            const csvFile = new Blob([fileContent], { type: 'text/csv' });
            const url = window.URL.createObjectURL(csvFile);
            const link = document.createElement('a');
            link.download = `${this.savedTableName}.csv`;
            link.href = url;
            link.click();
          
        }
      }
    });
  }

  makePlainHtmlTable(): string {
    if (!this.treeTable) return '';
    const columns = this.treeTable.displayedColumns.slice();
    const rows = this.treeTable['treeTable'].map(node =>
      `\t\t<tr class="mat-row">
    \t\t\t${columns.map(key => `<td class="mat-cell">${key === this.labelKey ? '&nbsp'.repeat(node.depth * 2) : ''}${node.value[key] || ''}</td>`).join('')}
\t\t</tr>\n`
    );
    if (this.firstColumnHeader) { columns[0] = this.firstColumnHeader; }
    const table = `
<table>
    <thead>
        <tr class="mat-header-row">
            ${columns.map(column => `<th>${this.translation.get(column)}</th>`).join('')}
        </tr>
    </thead>
    <tbody>
        ${rows.join('')}
    </tbody>
</table>
    `;
    return table;
  }

  generateCSVData() {
    if (!this.treeTable) return;
    const columns = this.treeTable.displayedColumns.slice();
    const nodes = this.treeTable['treeTable'];
    let csv = '';

    csv += columns.map(column => this.translation.get(column)).join(',') + '\n';
    for (const node of nodes) {
      csv += columns.map(column => {
        if (node.value[column] && node.value[column].length === 1) {
          return node.value[column][0].replace(/,/g, '');
        } else if (node.value[column] && typeof node.value[column] !== 'string' && node.value[column].length > 1) {
          this.toasterService.error('more than one value in column');
        } else if (node.value[column]) {
          const temp = FormatUtil.removeScenarioTags(node.value[column].replace(/,/g, ''));
          return FormatUtil.removeSubSupTags(temp);
        }
      }).join(',') + '\n';
    }

    if (this.firstColumnHeader) { csv.replace(this.translation.get(this.labelKey), 'first'); }
    return csv;
  }

  

  private getParamsForData(data, key: string) {
    if (!data || !key) {
      return;
    }
    const result = data[key];
    if (result && result['formatValues']) {
      return { '%s': result['formatValues'] };
    }
  }

  private valuesOnlyContainsZeros(values): boolean {
    if (!Array.isArray(values)) { return false; }
    for (const x of values) {
      if (x !== 0) {
        return false;
      }
    }
    return true;
  }

  justifyTableOnCenter() {
    const columnsOrder = this.options['customColumnOrder'];
    return columnsOrder && columnsOrder.length <= 3;
  }

  setCurrencyUnit(currencyUnit: Unit) {
    if (!currencyUnit || currencyUnit.unitType !== '000000000000000000000020') { return; }
    this._lastCurrencyUnit = this._currencyUnit ? this._currencyUnit.copyWithoutUnitService() : this._currencyUnit;
    this._currencyUnit = currencyUnit;
    if (this._inputsData) { this.reprocessInputsData(); }
    if (this._resultsData) { this.reprocessResultsData(); }
  }

  setMeasurement(measurement: string) {
    if (!measurement) { return; }
    this._lastMeasurement = this._measurement;
    this._measurement = measurement;
    if (this._inputsData) { this.reprocessInputsData(); }
    if (this._resultsData) { this.reprocessResultsData(); }
  }

  setPeriod(period: string) {
    if (!period) { return; }
    this._lastPeriod = this._period;
    this._period = period;
    if (this._inputsData) { this.reprocessInputsData(); }
    if (this._resultsData) { this.reprocessResultsData(); }
  }

  getPrefixedId(id: string) {
    return !!this.idPrefix ? this.idPrefix + '_' + id : id;
  }
}
